import { Svg } from './chunk-LPTJKXJO.js';
import { bindMethod, def, Ease, ease4, clamp, round, lerp } from './chunk-QF7HVQZZ.js';
import { Raf, Delay } from './chunk-WILRAE77.js';

// src/anima.ts
var linearEase = (t) => t;
function hasKey(obj, key) {
  return obj != null && Object.prototype.hasOwnProperty.call(obj, key);
}
function toEls(selOrEl) {
  if (!selOrEl) return [];
  if (typeof selOrEl === "string") {
    return Array.from(document.querySelectorAll(selOrEl));
  }
  if (selOrEl?.nodeType === 1 || selOrEl === window || selOrEl === document)
    return [selOrEl];
  if (Array.isArray(selOrEl) || selOrEl?.length !== void 0 && selOrEl[0])
    return Array.from(selOrEl);
  return [];
}
function getAttr(el, name) {
  return el?.getAttribute(name) ?? "";
}
var Anima = class {
  constructor(cfg = {}) {
    bindMethod(this, [
      "_gRaf",
      "_run",
      "_uProp",
      "_uSvg",
      "_uLine"
    ]);
    this._raf = new Raf("anima", this._run.bind(this));
    this._delay = null;
    this._init(cfg);
  }
  _init(s) {
    const v = {
      el: toEls(s.el),
      elL: 0,
      e: { curve: s.e ?? "linear", calc: linearEase },
      d: { origin: s.d ?? 0, cur: 0 },
      de: s.de ?? 0,
      cb: s.cb ?? null,
      r: s.r ?? 2,
      pr: 0,
      prE: 0,
      elapsed: 0,
      u: () => {
      }
    };
    v.elL = v.el.length;
    if (hasKey(s, "u") && s.u) v.u = () => s.u(v);
    else if (hasKey(s, "svg")) v.u = this._uSvg.bind(this);
    else if (hasKey(s, "line")) v.u = this._uLine.bind(this);
    else v.u = this._uProp.bind(this);
    if (s.p) {
      v.prop = {};
      v.propI = {};
      const keys = Object.keys(s.p);
      v.propL = keys.length;
      let seenR = false;
      for (let i = 0; i < keys.length; i++) {
        const name = keys[i];
        const arr = s.p[name];
        v.prop[i] = {
          name,
          origin: { start: arr[0], end: arr[1] },
          cur: arr[0],
          start: arr[0],
          end: arr[1],
          unit: arr[2] ?? "%"
        };
        const ch0 = name.charAt(0);
        const indexName = ch0 === "r" && seenR ? "r2" : ch0;
        seenR = ch0 === "r";
        v.propI[indexName] = i;
      }
    }
    if (s.svg) {
      const firstEl = v.el[0] ?? null;
      v.svg = {
        type: s.svg.type,
        attr: s.svg.type === "polygon" ? "points" : "d",
        end: s.svg.end,
        start: s.svg.start ?? getAttr(firstEl, s.svg.type === "polygon" ? "points" : "d"),
        cur: "",
        originArr: { start: [], end: [] },
        arr: { start: [], end: [] },
        arrL: 0,
        val: []
      };
      v.svg.cur = v.svg.start;
      v.svg.originArr.start = Svg.split(v.svg.start);
      v.svg.originArr.end = Svg.split(v.svg.end);
      v.svg.arr.start = v.svg.originArr.start.slice();
      v.svg.arr.end = v.svg.originArr.end.slice();
      v.svg.arrL = v.svg.arr.start.length;
    }
    if (s.line) {
      v.line = {
        dashed: s.line.dashed,
        factor: {
          start: def(s.line.start) ? (100 - (s.line.start ?? 0)) / 100 : 1,
          end: def(s.line.end) ? (100 - (s.line.end ?? 0)) / 100 : 0
        },
        shapeL: [],
        origin: { start: [], end: [] },
        cur: [],
        start: [],
        end: []
      };
      for (let e = 0; e < v.elL; e++) {
        const el = v.el[e];
        const n = s.line.elWL ?? el;
        v.line.shapeL[e] = Svg.shapeLength(n ?? null);
        let dashArr;
        if (v.line.dashed) {
          const parts = String(v.line.dashed).split(/[\s,]/);
          let segLen = 0;
          for (const p of parts) segLen += parseFloat(p) || 0;
          const repeat = Math.max(
            1,
            Math.ceil((v.line.shapeL[e] ?? 0) / (segLen || 1))
          );
          dashArr = new Array(repeat).fill(v.line.dashed).join(" ") + " 0 " + (v.line.shapeL[e] ?? 0);
        } else {
          dashArr = String(v.line.shapeL[e] ?? 0);
        }
        if (el) el.style.strokeDasharray = dashArr;
        v.line.origin.start[e] = v.line.factor.start * (v.line.shapeL[e] ?? 0);
        v.line.origin.end[e] = v.line.factor.end * (v.line.shapeL[e] ?? 0);
        v.line.cur[e] = v.line.origin.start[e] ?? 0;
        v.line.start[e] = v.line.origin.start[e] ?? 0;
        v.line.end[e] = v.line.origin.end[e] ?? 0;
      }
    }
    const easeCurve = typeof v.e.curve === "string" ? Ease[v.e.curve] ?? linearEase : ease4(Array.isArray(v.e.curve) ? v.e.curve : []);
    v.e.calc = easeCurve;
    this.v = v;
  }
  play(opts = {}) {
    this.pause();
    this._prepare(opts);
    if (this.v.de > 0) {
      this._delay = new Delay(() => this._gRaf(), this.v.de);
      this._delay.run();
    } else {
      this._gRaf();
    }
  }
  pause() {
    this._raf.stop();
    if (this._delay) this._delay.stop();
  }
  _prepare(s = {}) {
    const v = this.v;
    const toKey = hasKey(s, "reverse") ? "start" : "end";
    if (v.prop && v.propL) {
      for (let i = 0; i < v.propL; i++) {
        const prop = v.prop[i];
        if (!prop) continue;
        prop.end = prop.origin[toKey];
        prop.start = prop.cur;
        if (hasKey(s, "p") && s.p && hasKey(s.p, prop.name)) {
          const cfg = s.p[prop.name];
          if (cfg && hasKey(cfg, "newEnd") && cfg.newEnd !== void 0)
            prop.end = cfg.newEnd;
          if (cfg && hasKey(cfg, "newStart") && cfg.newStart !== void 0)
            prop.start = cfg.newStart;
        }
      }
    } else if (v.svg) {
      v.svg.arr.start = hasKey(s, "svg") && s.svg && hasKey(s.svg, "start") && s.svg.start ? s.svg.start : Svg.split(v.svg.cur);
      v.svg.arr.end = hasKey(s, "svg") && s.svg && hasKey(s.svg, "end") && s.svg.end ? s.svg.end : v.svg.originArr[toKey];
    } else if (v.line) {
      for (let i = 0; i < v.elL; i++)
        v.line.start[i] = v.line.cur[i] ?? 0;
      if (hasKey(s, "line") && s.line && hasKey(s.line, "end") && s.line.end !== void 0) {
        v.line.factor.end = (100 - s.line.end) / 100;
        for (let i = 0; i < v.elL; i++)
          v.line.end[i] = v.line.factor.end * (v.line.shapeL[i] ?? 0);
      } else {
        for (let i = 0; i < v.elL; i++)
          v.line.end[i] = v.line.origin[toKey][i] ?? 0;
      }
    }
    v.d.cur = hasKey(s, "d") && s.d !== void 0 ? s.d : Math.max(0, v.d.origin - v.d.cur + v.elapsed);
    v.e.curve = s.e ?? v.e.curve;
    v.e.calc = typeof v.e.curve === "string" ? Ease[v.e.curve] ?? linearEase : ease4(v.e.curve);
    v.de = hasKey(s, "de") && s.de !== void 0 ? s.de : v.de;
    v.cb = hasKey(s, "cb") && s.cb !== void 0 ? s.cb : v.cb;
    v.pr = v.prE = v.d.cur === 0 ? 1 : 0;
  }
  _gRaf() {
    this._raf.run();
  }
  _run(elapsedMs) {
    const v = this.v;
    if (v.pr === 1) {
      this.pause();
      v.u();
      if (v.cb)
        try {
          v.cb();
        } catch {
        }
      return;
    }
    v.elapsed = clamp(elapsedMs, 0, v.d.cur);
    v.pr = clamp(v.elapsed / v.d.cur, 0, 1);
    v.prE = v.e.calc(v.pr);
    v.u();
  }
  _uProp() {
    const v = this.v;
    const t = v.prop;
    const idx = v.propI;
    if (!t || !idx || !v.propL) return;
    for (let i = 0; i < v.propL; i++) {
      const prop = t[i];
      if (prop) prop.cur = this._lerp(prop.start, prop.end);
    }
    const xIdx = idx["x"];
    const yIdx = idx["y"];
    const rIdx = idx["r"];
    const r2Idx = idx["r2"];
    const sIdx = idx["s"];
    const oIdx = idx["o"];
    const xProp = xIdx !== void 0 ? t[xIdx] : void 0;
    const yProp = yIdx !== void 0 ? t[yIdx] : void 0;
    const rProp = rIdx !== void 0 ? t[rIdx] : void 0;
    const r2Prop = r2Idx !== void 0 ? t[r2Idx] : void 0;
    const sProp = sIdx !== void 0 ? t[sIdx] : void 0;
    const oProp = oIdx !== void 0 ? t[oIdx] : void 0;
    const x = xProp ? xProp.cur + xProp.unit : 0;
    const y = yProp ? yProp.cur + yProp.unit : 0;
    const tr = x || y ? `translate3d(${x || 0}, ${y || 0}, 0)` : 0;
    const r = rProp ? `${rProp.name}(${rProp.cur}deg)` : 0;
    const r2 = r2Prop ? `${r2Prop.name}(${r2Prop.cur}deg)` : 0;
    const s = sProp ? `${sProp.name}(${sProp.cur})` : 0;
    const o = oProp ? oProp.cur : -1;
    const transform = [tr, r, r2, s].filter(Boolean).join(" ");
    for (let n = 0; n < v.elL; n++) {
      const el = v.el[n];
      if (!el) continue;
      if (transform) el.style.transform = transform;
      if (o >= 0) el.style.opacity = String(o);
    }
  }
  _uSvg() {
    const s = this.v.svg;
    if (!s) return;
    s.curTemp = "";
    for (let i = 0; i < s.arrL; i++) {
      const startVal = s.arr.start[i];
      const endVal = s.arr.end[i];
      s.val[i] = isNaN(Number(startVal)) ? startVal ?? "" : this._lerp(Number(startVal), Number(endVal));
    }
    for (let i = 0; i < s.arrL; i++) {
      s.curTemp += (i && i % 2 === 0 ? " " : i ? "," : "") + String(s.val[i]);
    }
    s.cur = s.curTemp;
    for (let i = 0; i < this.v.elL; i++) {
      const target = this.v.el[i];
      target?.setAttribute(s.attr, s.cur);
    }
  }
  _uLine() {
    const v = this.v;
    if (!v.line) return;
    for (let i = 0; i < v.elL; i++) {
      v.line.cur[i] = this._lerp(v.line.start[i] ?? 0, v.line.end[i] ?? 0);
      const el = v.el[i];
      if (el) el.style.strokeDashoffset = String(v.line.cur[i]);
    }
  }
  _lerp(a, b) {
    return round(lerp(a, b, this.v.prE), this.v.r);
  }
};
var anima_default = Anima;

export { Anima, anima_default };
//# sourceMappingURL=chunk-ZFMW2YEI.js.map
//# sourceMappingURL=chunk-ZFMW2YEI.js.map